/*
 * Local N-way conference, mixed in the browser with WebAudio: each member
 * receives our microphone plus every other member, never itself (mix-minus),
 * the same semantics as the native core's conference.* commands. Chosen over
 * a PBX conference room: works with any PBX, no room to provision, and
 * split/rejoin are instant (docs/architecture.md).
 */
import type { CallRec, Core } from "./core";
import { CommandError, type Json } from "./types";

export interface Mixer {
  /** (re)build the mix for these members; called on every change */
  rebuild(members: CallRec[]): Promise<void>;
  /** give a call its own microphone back */
  release(rec: CallRec): Promise<void>;
}

export class Conference {
  private ids = new Set<string>();
  mixer?: Mixer;

  constructor(private core: Core) {}

  has(r: CallRec): boolean {
    return this.ids.has(r.id);
  }

  together(a: CallRec, b: CallRec): boolean {
    return this.ids.has(a.id) && this.ids.has(b.id);
  }

  members(): string[] {
    return [...this.ids];
  }

  async merge(ids?: string[]): Promise<Json> {
    const calls = this.core.allCalls().filter(
      (r) => r.state === "established" && (!ids || ids.includes(r.id)),
    );
    if (calls.length < 2) throw new CommandError("not_enough_calls");
    for (const r of calls) this.ids.add(r.id);
    for (const r of calls) await this.core.setHold(r, false, false);
    await this.remix();
    this.changed();
    return { calls: this.members() };
  }

  async add(rec: CallRec): Promise<Json> {
    if (rec.state !== "established") throw new CommandError("invalid");
    if (this.ids.size === 0) throw new CommandError("no_conference");
    this.ids.add(rec.id);
    await this.core.setHold(rec, false, false);
    await this.remix();
    this.changed();
    return { calls: this.members() };
  }

  /* Talk to that member alone; the rest of the conference is held. */
  async split(rec: CallRec): Promise<Json> {
    if (!this.ids.has(rec.id)) throw new CommandError("invalid");
    this.ids.delete(rec.id);
    await this.mixer?.release(rec);
    for (const id of this.ids) {
      const r = this.core.callRec(id);
      if (r) {
        this.core.silence(r);
        await this.core.setHold(r, true, false);
      }
    }
    await this.core.setHold(rec, false, true);
    await this.remix();
    this.changed();
    return { calls: this.members() };
  }

  async end(): Promise<void> {
    const ids = [...this.ids];
    this.ids.clear();
    for (const id of ids) {
      await this.core.callRec(id)?.stack.hangup().catch(() => undefined);
    }
    this.changed();
  }

  /* core: a call ended */
  removed(rec: CallRec): void {
    if (!this.ids.delete(rec.id)) return;
    if (this.ids.size < 2) {
      for (const id of this.ids) {
        const r = this.core.callRec(id);
        if (r) void this.mixer?.release(r);
      }
      this.ids.clear();
    } else {
      void this.remix();
    }
    this.changed();
  }

  private async remix(): Promise<void> {
    if (!this.mixer) return;
    const members = this.members()
      .map((id) => this.core.callRec(id))
      .filter((r): r is CallRec => !!r);
    await this.mixer.rebuild(members);
  }

  private changed(): void {
    this.core.event("conference.changed", { calls: this.members() });
  }
}

/* The WebAudio implementation used in browsers. */
export class WebAudioMixer implements Mixer {
  private ctx?: AudioContext;
  private nodes: AudioNode[] = [];

  async rebuild(members: CallRec[]): Promise<void> {
    this.teardown();
    if (members.length < 2) {
      for (const m of members) await this.release(m);
      return;
    }
    this.ctx ??= new AudioContext();
    const ctx = this.ctx;
    const mic = members[0].stack.localStream();
    const micNode = mic ? ctx.createMediaStreamSource(mic) : undefined;
    const remotes = members.map((m) => {
      const s = m.stack.remoteStream();
      return s ? ctx.createMediaStreamSource(s) : undefined;
    });
    if (micNode) this.nodes.push(micNode);
    remotes.forEach((n) => n && this.nodes.push(n));

    for (let i = 0; i < members.length; i++) {
      const dest = ctx.createMediaStreamDestination();
      this.nodes.push(dest);
      if (micNode && !members[i].muted) micNode.connect(dest);
      remotes.forEach((n, j) => {
        if (n && j !== i) n.connect(dest);
      });
      await members[i].stack.replaceOutgoing(dest.stream.getAudioTracks()[0]);
    }
  }

  async release(rec: CallRec): Promise<void> {
    await rec.stack.replaceOutgoing(null);
  }

  private teardown(): void {
    for (const n of this.nodes) {
      try {
        n.disconnect();
      } catch {
        /* already gone */
      }
    }
    this.nodes = [];
  }
}
